import { Grid } from "@mui/material";
import {
  CommissionDetail,
  RFP_CURRENT_COMMISSION_OPTIONS,
  RFP_REQUESTED_COMMISSION_OPTIONS,
} from "@platform/formatting-utils";
import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { COVERAGE_FORM } from "@platform/rfp-core";

import {
  validateCurrentCommission,
  validateRequestedCommission,
} from "../../coverages/CoverageForms/commissionValidation";
import { SectionTitle } from "../styledComponents";

import { CommissionField } from "./CommissionField";

const {
  CURRENT_COMMISSION,
  CURRENT_COMMISSION_DETAIL,
  REQUESTED_COMMISSION,
  REQUESTED_COMMISSION_DETAIL,
  SINGLE_PLAN,
} = COVERAGE_FORM;

interface PlanFormTarget {
  name: string;
  value: unknown;
}

export interface NormalizedCommissionSectionProps {
  handlePlanFormChange: (target: PlanFormTarget, planName: string) => void;
  /** Stores and validates. Absent in stories and older callers. */
  handlePlanFormChangeWithValidation?: (
    target: PlanFormTarget,
    planName: string,
  ) => void;
  /** Validates the fields ordered above the one being entered. */
  handlePreviousFieldsValidation?: (field: string, planName: string) => void;
  currentPlanErrors: Record<string, string>;
  currentPlan: Record<string, unknown>;
  planName?: string;
  isCurrentCoverage?: boolean;
}

/**
 * Commission entry as a normalized dropdown plus typed detail.
 *
 * Form state holds only the discrete detail. The legacy free-text field is
 * derived from it when the RFP is submitted, so the two can never disagree in a
 * draft — and so each change stays a single call, since the form's change
 * handler rebuilds every plan from a snapshot and would lose the first of two.
 */
export const NormalizedCommissionSection = ({
  handlePlanFormChange,
  handlePlanFormChangeWithValidation,
  handlePreviousFieldsValidation,
  currentPlanErrors,
  currentPlan,
  planName = SINGLE_PLAN,
  isCurrentCoverage,
}: NormalizedCommissionSectionProps): JSX.Element => {
  // Fields whose stored error predates the type now selected. Changing the type
  // starts a fresh slate: the broker has not been told anything yet about the
  // type they just picked. The next validating write ends it.
  //
  // Withholding the message never lets an incomplete commission through — the
  // step gate recomputes validity from the plan and ignores these entirely.
  // Deleting the stored errors instead would take a second write, and the form
  // rebuilds every plan from one snapshot, so the pair would lose the first.
  const [awaitingRecheck, setAwaitingRecheck] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const scoped = useCallback(
    (detailKey: string): string => `${planName}:${detailKey}`,
    [planName],
  );

  const change = useCallback(
    (detailKey: string, detail: CommissionDetail): void => {
      const stored = currentPlan[detailKey] as CommissionDetail | undefined;

      if (detail.type !== stored?.type) {
        setAwaitingRecheck((keys) => new Set(keys).add(scoped(detailKey)));
      }

      handlePlanFormChange({ name: detailKey, value: detail }, planName);
    },
    [currentPlan, handlePlanFormChange, planName, scoped],
  );

  // Stores and validates in one call, so a value that blocks the step also
  // explains why. Falls back to the plain handler where none is supplied.
  const commit = useCallback(
    (detailKey: string, detail: CommissionDetail): void => {
      if (handlePlanFormChangeWithValidation) {
        handlePlanFormChangeWithValidation(
          { name: detailKey, value: detail },
          planName,
        );
      } else {
        change(detailKey, detail);
      }

      setAwaitingRecheck((keys) => {
        const remaining = new Set(keys);
        remaining.delete(scoped(detailKey));
        return remaining;
      });
    },
    [change, handlePlanFormChangeWithValidation, planName, scoped],
  );

  // The field order these validate against is keyed by the legacy field names.
  const enter = useCallback(
    (legacyKey: string): void => {
      handlePreviousFieldsValidation?.(legacyKey, planName);
    },
    [handlePreviousFieldsValidation, planName],
  );

  // Entering the requested field means the broker has moved past the current
  // one, so that is when it gets checked. The shared field ordering cannot be
  // relied on for this: several coverages are missing from its supported list,
  // and it never checks the field being entered. Re-committing the detail
  // unchanged validates it without altering what was captured.
  const enterRequested = useCallback((): void => {
    enter(REQUESTED_COMMISSION);

    if (!isCurrentCoverage) {
      return;
    }

    commit(
      CURRENT_COMMISSION_DETAIL,
      (currentPlan[CURRENT_COMMISSION_DETAIL] as CommissionDetail) ?? {},
    );
  }, [commit, currentPlan, enter, isCurrentCoverage]);

  const seededKeys = useRef<Set<string>>(new Set());

  // Validation reads the discrete detail whenever the key exists and the free
  // text otherwise, which keeps it free of any flag check. Seeding the key here
  // is what tells it to: a coverage captured as free text before this change
  // still has to be re-picked from the dropdown once it is edited.
  //
  // One key per pass. The form's change handler rebuilds every plan from the
  // snapshot it closed over, so seeding both in the same pass would discard the
  // first; this runs again as each one lands. The ref stops a key being
  // attempted twice, so a caller that drops the change cannot loop.
  useEffect(() => {
    const unseeded = [
      ...(isCurrentCoverage ? [CURRENT_COMMISSION_DETAIL] : []),
      REQUESTED_COMMISSION_DETAIL,
    ].find(
      (key) =>
        currentPlan[key] === undefined &&
        !seededKeys.current.has(`${planName}:${key}`),
    );

    if (!unseeded) {
      return;
    }

    seededKeys.current.add(`${planName}:${unseeded}`);
    change(unseeded, {});
  }, [change, currentPlan, isCurrentCoverage, planName]);

  /**
   * The message a flagged commission shows.
   *
   * Stored errors say whether the broker has been told about this side; the
   * plan says what is wrong with it now. Neither key is rewritten as the value
   * changes — the shared field ordering only ever adds to the legacy key, and
   * never revisits it — so a stored message outlives the type it described.
   */
  const errorFor = (
    detailKey: string,
    legacyKey: string,
    validate: (plan: Record<string, unknown>) => string | false,
  ): string | undefined => {
    if (awaitingRecheck.has(scoped(detailKey))) {
      return undefined;
    }

    const flagged =
      currentPlanErrors[detailKey] ?? currentPlanErrors[legacyKey];

    return flagged ? validate(currentPlan) || undefined : undefined;
  };

  return (
    <>
      <SectionTitle>Commission Information</SectionTitle>
      <Grid container spacing={2} px={2} data-testid="commission-section">
        {isCurrentCoverage && (
          <Grid size={{ xs: 12, md: 6 }}>
            <CommissionField
              // Tabbed coverages render one subtree for every plan, so the
              // field is recreated per plan rather than carrying its editor
              // state across the tabs.
              key={planName}
              label="Current Commission"
              testId="current-commission"
              required
              options={RFP_CURRENT_COMMISSION_OPTIONS}
              value={currentPlan[CURRENT_COMMISSION_DETAIL] as CommissionDetail}
              error={errorFor(
                CURRENT_COMMISSION_DETAIL,
                CURRENT_COMMISSION,
                validateCurrentCommission,
              )}
              onChange={(detail): void =>
                change(CURRENT_COMMISSION_DETAIL, detail)
              }
              onCommit={(detail): void =>
                commit(CURRENT_COMMISSION_DETAIL, detail)
              }
              onEnter={(): void => enter(CURRENT_COMMISSION)}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <CommissionField
            key={planName}
            label="Requested Commission"
            testId="requested-commission"
            options={RFP_REQUESTED_COMMISSION_OPTIONS}
            value={currentPlan[REQUESTED_COMMISSION_DETAIL] as CommissionDetail}
            error={errorFor(
              REQUESTED_COMMISSION_DETAIL,
              REQUESTED_COMMISSION,
              validateRequestedCommission,
            )}
            onChange={(detail): void =>
              change(REQUESTED_COMMISSION_DETAIL, detail)
            }
            onCommit={(detail): void =>
              commit(REQUESTED_COMMISSION_DETAIL, detail)
            }
            onEnter={enterRequested}
          />
        </Grid>
      </Grid>
    </>
  );
};
